import * as React from 'react';
import { Flex, Box } from 'grid-styled';

import {
  getValidators,
  inputIsEmpty,
} from '../../../utils/validation/validators';
import {
  getErrorMessage,
  errorMessageMappingType,
} from '../../../utils/validation/errorMessages';
import {
  DateInputProps,
  TextInputProps,
  SelectFieldInputProps,
} from '../Inputs/types';

import DateInput from '../Inputs/Date/DateInput';
import PhoneInput from '../Inputs/Phone/PhoneInput';
import PasswordInput from '../Inputs/Password/PasswordInput';
import StateInput from '../Inputs/Address/StateInput';
import TextInput from '../Inputs/TextInput';
import SelectFieldInput from '../Inputs/SelectFieldInput';

import './TextInputValidator.scss';

const supportedComponentTypes = {
  DateInput,
  SelectFieldInput,
  PasswordInput,
  PhoneInput,
  StateInput,
  TextInput,
};

interface TextInputValidatorProps {
  input: any; // Could be a string, or a dict of strings... reference the input component to see.
  onValidate: (id: string, errorEnum: string) => void;
  id: string; // Make the id a unique descriptor for the input's purpose in the form, like 'birthdate'.
  componentType: string; // See supportedComponentTypes above.
  boxWidth?: Array<number>; // For custom <Box w={boxWidth}>. Boxes with <Flex column=true> are full width by default.
  showError: boolean; // Whether or not to show error text in this component.
  errorAlign?: string; // Where to place error message in relation to the input. Usually bottom.
  errorEnum: string; // The current error; see errorMessages.js for a list of them.
  validators: Array<string>; // The validators to run on the input, e.g., ['required', 'validDate']. See validators.js
  messageOverride?: errorMessageMappingType; // Overwrite the standard error message with something custom.
  className?: string;
  errorMessageOverride?: any;
  // Note: Other props like callbacks may be passed through and used in the InputComponent.
  // They are combined with TextInputValidatorProps below, to form CombinedProps.
}

interface TextInputValidatorState {
  validators: {};
}

// Extend the TextInputValidator's props to include all possible props of the wrapped component.
type CombinedProps = TextInputValidatorProps &
  (TextInputProps | SelectFieldInputProps | DateInputProps);

export default class TextInputValidator extends React.Component<
  CombinedProps,
  TextInputValidatorState
> {
  static defaultProps: Partial<CombinedProps> = {
    errorAlign: 'bottom',
  };

  constructor(props: CombinedProps) {
    super(props);
    this.state = {
      validators: getValidators(props.componentType) || {},
    };
    // Do initial validation on load in case they try to submit without making any changes.
    this.validate(this.props);
  }

  UNSAFE_componentWillReceiveProps(nextProps: CombinedProps) {
    // Run validation any time the input changes.
    if (this.props.input !== nextProps.input) {
      this.validate(nextProps);
    }
  }

  validate = (props: CombinedProps): void => {
    let err = '';
    const validators = props.validators || [];
    // If the field is optional and empty, we don't need to validate.
    const optionalInput = validators.indexOf('required') === -1;
    if (inputIsEmpty(props.componentType, props.input) && optionalInput) {
      // Update the parent that there is no error here if an optional input is empty.
      this.onValidateError(err);
      return;
    }

    // If there is something in the input or the field is required, run all the validations.
    // Go through each of the requested validators in order, and run them against the input.
    for (let i = 0; i < validators.length; i += 1) {
      const validator = validators[i];
      // You can pass paramters into some validators via the syntax 'validatorName param1 param2'
      const validatorParams = validator.split(' ');
      const validatorName = validatorParams[0];
      validatorParams.splice(0, 1);

      // Execute the validation and stop at first error.
      const validatorFn = this.state.validators[validatorName];
      if (validatorFn === undefined) {
        // If we cannot find a requested validator, log an error in the console but skip the validation.
        console.error(
          `Could not find validator "${validator}" for ${props.id}`,
        );
      } else {
        // Pass input and any extra params.
        err = validatorFn(props.input, ...validatorParams);
        if (err) {
          break;
        }
      }
    }
    this.onValidateError(err);
  };

  onValidateError = (err: string): void => {
    // As we are not guaranteed that the parent returns undefined, null, '',
    // we will just exit early if both essentially have no error.
    if (!err && !this.props.errorEnum) return;

    // If the error has changed, call the callback function.
    if (this.props.errorEnum !== err) {
      this.props.onValidate(this.props.id, err);
    }
  };

  render() {
    const {
      errorEnum,
      showError,
      messageOverride,
      errorAlign,
      componentType,
      boxWidth,
    } = this.props;

    const InputComponent = supportedComponentTypes[componentType];

    const errorText =
      (showError && getErrorMessage(errorEnum, messageOverride)) || '';

    // horizontal stacking
    const errorOnRight = errorAlign === 'right';
    const paddingLeft = errorOnRight ? 2 : ''; // Usually equals 16px.
    const boxAlign = errorOnRight ? 'center' : 'baseline';

    // vertical stacking
    const errorOnBottom = errorAlign === 'bottom';
    const paddingBottom = errorOnBottom ? '18px' : '';
    const width = boxWidth || (errorOnBottom ? [1] : []);

    return (
      <div className="TextInputValidator">
        <Flex column={errorOnBottom} align={boxAlign}>
          <Box w={width}>
            <InputComponent {...this.props} showError={errorEnum && showError}>
              {this.props.children}
            </InputComponent>
          </Box>
          {errorText && (
            <Box
              className="input-errorText"
              pl={paddingLeft}
              pb={paddingBottom}
            >
              {errorText}
            </Box>
          )}
        </Flex>
      </div>
    );
  }
}
